import { Box } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import type { ChangeEvent, FC } from "react"
import { useState } from "react"

import { AdjustmentHistoryActionType } from "../constants"
import { useAdjustmentHistoryFilterOptions } from "../hooks/useAdjustmentHistoryFilterOptions"
import { useAdjustmentHistoryList } from "../hooks/useAdjustmentHistoryList"
import { useAdjustmentPalletLabelPrinting } from "../hooks/useAdjustmentPalletLabelPrinting"
import { useConfirmAdjustmentWorkOrder } from "../hooks/useConfirmAdjustmentWorkOrder"
import type { AdjustmentHistoryAction } from "../types"
import { getPalletReferenceNumbersForPrint } from "../utils"

import { AdjustmentHistoryDocumentsDialog } from "./AdjustmentHistoryDocumentsDialog"
import { AdjustmentHistoryFilterChips } from "./AdjustmentHistoryFilterChips"
import { AdjustmentHistoryFilterDrawer } from "./AdjustmentHistoryFilterDrawer"
import { AdjustmentHistoryTable } from "./AdjustmentHistoryTable"
import { AdjustmentWorkOrderPrintView } from "./AdjustmentWorkOrderPrintView"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import { PermissionActionType } from "@/constants/permissions"
import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useWorkOrderPrint } from "@/screens/Inventory/hooks"

export const AdjustmentHistoryListPage: FC = () => {
  const [activeAction, setActiveAction] = useState<AdjustmentHistoryAction | null>(null)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.INVENTORY_MAINTENANCE])

  const { confirmWorkOrder } = useConfirmAdjustmentWorkOrder()
  const { printPalletLabels, isPrinting } = useAdjustmentPalletLabelPrinting()

  const { printViewRef, printWorkOrder } = useWorkOrderPrint("Inventory Adjustment Work Order")

  const resetAction = () => setActiveAction(null)

  const handleActionClose = () => resetAction()

  const handleWorkOrderPrint = (item: AdjustmentHistoryItem) => {
    printWorkOrder(() => setActiveAction({ type: AdjustmentHistoryActionType.WorkOrderPrint, item }))
  }

  const {
    adjustmentHistoryItems,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    refetch,
    handleSortingChange,
    handleExpandedChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleScroll,
  } = useAdjustmentHistoryList()

  const handleWorkOrderConfirm = async (item: AdjustmentHistoryItem) => {
    const isSuccess = await confirmWorkOrder(item.inventoryAdjustmentId)
    if (isSuccess) refetch()
  }

  const handlePalletLabelPrint = (item: AdjustmentHistoryItem, palletReferenceNumber: number) =>
    printPalletLabels(item.product.productId, [palletReferenceNumber])

  const handlePalletLabelPrintAll = (item: AdjustmentHistoryItem) =>
    printPalletLabels(item.product.productId, getPalletReferenceNumbersForPrint(item.lots))

  const filterOptions = useAdjustmentHistoryFilterOptions()

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by Product ID, Lot #, Pallet #, Adjustment #, or Description'
          defaultValue={searchParams.query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          className='w-full max-w-[524px]'
          data-testid='adjustment-history.list-page.search-field'
        />
        <Button
          appearance='outlined'
          startIcon={<FilterAltOutlined />}
          onClick={() => setIsFilterDrawerOpen(true)}
          data-testid='adjustment-history.list-page.filter-button'
        >
          Filter
        </Button>
      </Box>

      <AdjustmentHistoryFilterChips {...filterOptions} searchParams={searchParams} onRemove={handleFilterRemove} />

      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <AdjustmentHistoryTable
          data={adjustmentHistoryItems}
          loading={isFetching}
          error={error}
          sorting={sorting}
          expanded={expanded}
          canMaintain={canMaintain}
          isPrinting={isPrinting}
          containerRef={tableContainerRef}
          onScroll={handleScroll}
          onSortingChange={handleSortingChange}
          onExpandedChange={handleExpandedChange}
          onDocumentsOpen={(item) => setActiveAction({ type: AdjustmentHistoryActionType.DocumentsView, item })}
          onWorkOrderConfirm={handleWorkOrderConfirm}
          onWorkOrderPrint={handleWorkOrderPrint}
          onPalletLabelPrint={handlePalletLabelPrint}
          onPalletLabelPrintAll={handlePalletLabelPrintAll}
        />
      </Box>

      <AdjustmentHistoryFilterDrawer
        {...filterOptions}
        open={isFilterDrawerOpen}
        searchParams={searchParams}
        onToggle={() => setIsFilterDrawerOpen(false)}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
      />

      {activeAction?.type === AdjustmentHistoryActionType.DocumentsView && (
        <AdjustmentHistoryDocumentsDialog item={activeAction.item} onClose={handleActionClose} />
      )}

      {activeAction?.type === AdjustmentHistoryActionType.WorkOrderPrint && (
        <AdjustmentWorkOrderPrintView ref={printViewRef} item={activeAction.item} />
      )}
    </Box>
  )
}
